import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Currency, DataTypeOption, DataTypeOptions } from '@core/constants/data-types.constants';
import { CurrencyConstraints } from '@core/models/config.model';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { convert, CurrencyExchange } from '@core/models/currency-exchange';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import { Nullable } from '@shared/utils/mapped-types.utils';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mfp-input-currency',
  templateUrl: './input-currency.component.html',
  styleUrls: ['./input-currency.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCurrencyComponent),
      multi: true
    }
  ]
})
export class InputCurrencyComponent implements ControlValueAccessor, OnDestroy {
  private readonly DefaultThousandSeparator = '.';
  private readonly DefaultDecimalMarker = ',';

  @Input() view = 'agent';
  @Input() name = '';
  @Input() label = '';
  @Input() currencyOptions: DataTypeOptions<Currency> = [];
  @Input() required = false;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() success = false;
  @Input() error = false;
  @Input() helper = '';

  inputFocussed = false;
  showOptions = false;

  currencyExchange?: CurrencyExchange;
  currencyConstraints?: CurrencyConstraints;
  selectedCurrency = '';
  private innerValue: Nullable<CurrencyAmount> = {
    currency: null,
    amount: null
  };

  $storeSubscription: Subscription;

  constructor(private store: Store<AppState>) {
    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.currencyExchange = state.config.currencyExchange;
        this.currencyConstraints = state.config.currencyConstraints;
      }
    });
  }

  ngOnDestroy() {
    this.$storeSubscription.unsubscribe();
  }

  setFocus(): void {
    this.inputFocussed = true;
  }

  loseFocus(): void {
    this.inputFocussed = false;
  }

  toggleOptions(): void {
    if (!this.readonly && !this.disabled) {
      this.showOptions = !this.showOptions;
    }
  }

  selectOption(option: DataTypeOption<Currency>): void {
    this.selectedCurrency = option.value;
    // Convert the currency value only if it exists
    if (this.innerValue.amount && this.innerValue.currency) {
      this.value = {
        amount: this.currencyExchange
          ? convert(this.innerValue as CurrencyAmount, this.selectedCurrency, this.currencyExchange).amount
          : this.innerValue.amount,
        currency: option.value
      };
    } else {
      this.value = {
        amount: this.innerValue?.amount,
        currency: option.value
      };
    }
    this.toggleOptions();
  }

  /// ControlValueAccessor
  private onChangeCallback = (_: Nullable<CurrencyAmount>) => {};
  private onTouchCallback = () => {};

  get value(): Nullable<CurrencyAmount> {
    return this.innerValue;
  }

  set value(value: Nullable<CurrencyAmount>) {
    this.innerValue = value;
    const currencyByValue = this.currencyOptions.find(op => op.value === value.currency) as {
      key: string;
      value: Currency;
    };
    if (currencyByValue) {
      this.selectedCurrency = currencyByValue.key;
    }
    this.onChangeCallback(value);
    this.onTouchCallback();
  }

  get thousandSeparator() {
    return this.currencyConstraints?.masking?.thousandSeparator || this.DefaultThousandSeparator;
  }

  get decimalMarker() {
    const thousandSeparator = this.thousandSeparator;
    const decimalMarker = this.currencyConstraints?.masking?.decimalMarker || this.DefaultDecimalMarker;
    if (decimalMarker === thousandSeparator) {
      return thousandSeparator === '.' ? ',' : '.';
    }
    return decimalMarker;
  }

  get amountStr(): string | null {
    // This 'amount' value is inserted into the input with the 'mask' directive
    // so it is like if we write the text into (type = text). If we set a number
    // decimal value (e.g 12.34), then it is written with '.' (dots). Tf '.' is the
    // 'thousandSeparator', a value with 12.23 will be converted into 1234 (represented as 1.234).
    // To prevent this, we will override '.' with a comma if it the thousandSeparator
    // is a dot, since the mask directive changes the 'decimalMarker' to a 'comma' implicitly
    // by the same condition (if there is a 'dot' as the 'thousandSeparator').
    // We would use the 'decimalMarker' provided in the config, but it can only be a '.' or a ','
    // so, by replacing it directly with a ',', we prevent a bad configuration where
    // the 'thousandSeparator' is a '.' and the 'decimalMarker' is also a '.'.
    if (this.thousandSeparator === '.') {
      return `${this.innerValue.amount}`.replace('.', ',');
    }
    return `${this.innerValue.amount}`;
  }

  setAmountStr(amount: string | null) {
    if (amount && !isNaN(+amount)) {
      this.value = {
        amount: +amount,
        currency: this.innerValue.currency
      };
    } else {
      this.value = {
        amount: null,
        currency: this.innerValue.currency
      };
    }
  }

  writeValue(value: Nullable<CurrencyAmount>): void {
    if (value) {
      this.value = value;
    }
  }

  registerOnChange(fn: (_: Nullable<CurrencyAmount>) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchCallback = fn;
  }

  setDisabledState(state: boolean): void {
    this.disabled = state;
  }
}
